import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, TrendingUp, TrendingDown, Sparkles } from 'lucide-react';
import { apiFetch } from '../../../utils/api';
import { useToast } from '../../../components/ui/Toast';
import './inventory-tracker.css';

const formatMoney = (n) => `₦${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const formatNumber = (n) => Number(n || 0).toLocaleString();

/**
 * Insights tab — waste always, ABC + turnover premium-gated. The
 * backend returns `{available:false}` blocks for non-premium so we
 * always render the same three slots and show an upgrade prompt in the
 * gated ones. Single API call.
 */
const InsightsTab = () => {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [window, setWindow] = useState(90);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`/merchants/inventory/insights?window=${window}`);
      const payload = await res.json();
      if (!res.ok || !payload.success) throw new Error(payload.error || 'Unable to load insights');
      setData(payload);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [window, toast]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="itk-panel">
      <div className="itk-panel-header">
        <div>
          <h2>Insights</h2>
          <p>Waste, ABC classification, and turnover rate.</p>
        </div>
        <div className="itk-toolbar">
          <select value={window} onChange={(e) => setWindow(Number(e.target.value))} aria-label="Insights window">
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 180 days</option>
            <option value={365}>Last 12 months</option>
          </select>
          <button type="button" className="itk-btn" onClick={load} disabled={loading}>
            {loading ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />}
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="itk-loading"><Loader2 size={18} className="spin" /> Loading insights…</div>
      ) : !data ? null : (
        <>
          {/* Waste card */}
          <section className="itk-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#111827' }}>Waste &amp; loss</h3>
                <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: '#6b7280' }}>
                  {formatNumber(data.waste.totalUnits)} units · {formatMoney(data.waste.totalLoss)} loss over {data.waste.window.days} days
                </p>
              </div>
            </div>

            {data.waste.byReason.length === 0 ? (
              <div className="itk-empty"><strong>No waste recorded</strong>Nothing has been written off in this window.</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <div>
                  <p style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em', margin: '0 0 8px' }}>By reason</p>
                  <table className="itk-table" style={{ background: 'transparent' }}>
                    <thead>
                      <tr>
                        <th>Reason</th>
                        <th>Units</th>
                        <th>Loss</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.waste.byReason.map((row) => (
                        <tr key={row.reason}>
                          <td>{row.reason}</td>
                          <td>{formatNumber(row.units)}</td>
                          <td>{formatMoney(row.loss)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div>
                  <p style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em', margin: '0 0 8px' }}>Top offenders</p>
                  <table className="itk-table" style={{ background: 'transparent' }}>
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Units</th>
                        <th>Loss</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.waste.topProducts.map((row) => (
                        <tr key={row.productId}>
                          <td>{row.productName}</td>
                          <td>{formatNumber(row.units)}</td>
                          <td>{formatMoney(row.loss)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>

          {/* ABC card */}
          <section className="itk-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#111827' }}>ABC classification</h3>
                <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: '#6b7280' }}>
                  {data.abc.available
                    ? 'Products ranked by trailing 90-day revenue contribution.'
                    : 'Upgrade to Premium to unlock revenue-tier segmentation of your catalog.'}
                </p>
              </div>
            </div>

            {!data.abc.available ? (
              <div className="itk-upgrade">
                <Sparkles size={24} color="#0d7463" style={{ marginBottom: 8 }} />
                <strong>Premium feature</strong>
                <p>See which SKUs are your revenue leaders (class A) vs the long tail (class C) so you can prioritise stock decisions.</p>
              </div>
            ) : (
              <>
                <div className="itk-metrics" style={{ marginBottom: 12 }}>
                  {['A', 'B', 'C', 'UNCLASSIFIED'].map((cls) => (
                    <div key={cls} className="itk-card" style={{ padding: 12 }}>
                      <span className={`itk-badge class-${cls.toLowerCase()}`}>{cls === 'UNCLASSIFIED' ? 'No sales' : `Class ${cls}`}</span>
                      <div className="itk-card-value" style={{ marginTop: 6 }}>{data.abc.counts[cls] || 0}</div>
                    </div>
                  ))}
                </div>
                {['A', 'B', 'C'].map((cls) => (
                  <div key={cls} style={{ marginTop: 14 }}>
                    <p style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em', margin: '0 0 6px' }}>
                      Top {cls}-class SKUs
                    </p>
                    {(data.abc.topByClass?.[cls] || []).length === 0 ? (
                      <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: 0 }}>No products in this class.</p>
                    ) : (
                      <table className="itk-table" style={{ background: 'transparent' }}>
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>Qty</th>
                            <th>Velocity / day</th>
                            <th>Days of stock</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(data.abc.topByClass[cls] || []).map((p) => (
                            <tr key={p.id}>
                              <td>{p.name}</td>
                              <td>{formatNumber(p.quantity)}</td>
                              <td>{Number(p.velocity30d).toFixed(2)}</td>
                              <td>{p.daysOfStock ?? '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                ))}
              </>
            )}
          </section>

          {/* Turnover card */}
          <section className="itk-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#111827' }}>Inventory turnover</h3>
                <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: '#6b7280' }}>
                  {data.turnover.available
                    ? 'Cost of goods sold ÷ average inventory value, annualised.'
                    : 'Upgrade to Premium to see how quickly your inventory turns.'}
                </p>
              </div>
            </div>

            {!data.turnover.available ? (
              <div className="itk-upgrade">
                <Sparkles size={24} color="#0d7463" style={{ marginBottom: 8 }} />
                <strong>Premium feature</strong>
                <p>Compare your turnover to Nigerian retail benchmarks and see where cash is trapped.</p>
              </div>
            ) : (
              <div className="itk-metrics">
                <div className="itk-card">
                  <p className="itk-card-title">Cost of goods sold</p>
                  <div className="itk-card-value">{formatMoney(data.turnover.cogs)}</div>
                  <div className="itk-card-caption">{data.turnover.window.days} days</div>
                </div>
                <div className="itk-card">
                  <p className="itk-card-title">Avg inventory (at cost)</p>
                  <div className="itk-card-value">{formatMoney(data.turnover.avgInventoryAtCost)}</div>
                  <div className="itk-card-caption">Current active batches</div>
                </div>
                <div className="itk-card">
                  <p className="itk-card-title">Annualised turnover</p>
                  <div className="itk-card-value">
                    {data.turnover.annualizedRate != null ? `${data.turnover.annualizedRate}x` : '—'}
                  </div>
                  <div className="itk-card-caption">
                    Nigerian retail benchmark: {data.turnover.benchmark.nonPerishableLow}–{data.turnover.benchmark.fmcgHigh}x/year
                  </div>
                </div>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default InsightsTab;
